import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import axios from 'axios';

const AddPermissionRequest = ({ show, onHide, employees, salarySettings, onPermissionAdded }) => {
    const [formData, setFormData] = useState({
        emp_id: '',
        permission_date: new Date().toISOString().split('T')[0],
        from_time: '',
        to_time: '',
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [employeeLimits, setEmployeeLimits] = useState(null);
    const [weeklyOffDays, setWeeklyOffDays] = useState([]);

    // Autocomplete states
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const searchRef = useRef(null);
    const suggestionsRef = useRef(null);

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    // Filter employees based on search query
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredEmployees([]);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = employees
            .filter(emp => emp.status === 'Active')
            .filter(emp =>
                emp.first_name?.toLowerCase().includes(query) ||
                emp.last_name?.toLowerCase().includes(query) ||
                emp.emp_code?.toLowerCase().includes(query) ||
                emp.email?.toLowerCase().includes(query) ||
                `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(query)
            )
            .slice(0, 10); // Limit to 10 suggestions

        setFilteredEmployees(filtered);
        setShowSuggestions(filtered.length > 0);
    }, [searchQuery, employees]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update limits when employee changes
    useEffect(() => {
        if (formData.emp_id) {
            const settings = salarySettings.find(s => s.emp_id === parseInt(formData.emp_id));
            if (settings) {
                setEmployeeLimits({
                    permission_per_month: settings.permission_per_month || 0,
                    permission_hour: settings.permission_hour || 0,
                    min_hour_for_present: settings.min_hour_for_present || 0
                });

                // Set weekly off days
                const offDays = [];
                if (settings.weeklyoff_1) offDays.push(settings.weeklyoff_1);
                if (settings.weeklyoff_2) offDays.push(settings.weeklyoff_2);
                setWeeklyOffDays(offDays);
            } else {
                setEmployeeLimits({
                    permission_per_month: 0,
                    permission_hour: 0,
                    min_hour_for_present: 0
                });
                setWeeklyOffDays(['Sunday']);
            }
        } else {
            setEmployeeLimits(null);
            setWeeklyOffDays([]);
        }
    }, [formData.emp_id, salarySettings]);

    // Check if selected date is weekly off
    useEffect(() => {
        if (formData.permission_date && weeklyOffDays.length > 0) {
            const selectedDay = new Date(formData.permission_date).toLocaleDateString('en-US', { weekday: 'long' });
            if (weeklyOffDays.includes(selectedDay)) {
                setError(`Cannot request permission on weekly off day (${selectedDay})`);
            } else if (error && error.includes('weekly off day')) {
                setError('');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.permission_date, weeklyOffDays, error]);

    const handleEmployeeSelect = (employee) => {
        setFormData(prev => ({
            ...prev,
            emp_id: employee.emp_id
        }));
        setSelectedEmployee(employee);
        setSearchQuery(`${employee.first_name} ${employee.last_name} (${employee.emp_code})`);
        setShowSuggestions(false);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // If user clears the search, clear the selected employee
        if (!value.trim()) {
            setFormData(prev => ({ ...prev, emp_id: '' }));
            setSelectedEmployee(null);
            setShowSuggestions(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Auto-calculate total hours when both times are set
        if ((name === 'from_time' || name === 'to_time') && formData.from_time && formData.to_time) {
            const fromTime = name === 'from_time' ? value : formData.from_time;
            const toTime = name === 'to_time' ? value : formData.to_time;

            if (fromTime && toTime) {
                const from = new Date(`1970-01-01T${fromTime}`);
                const to = new Date(`1970-01-01T${toTime}`);
                const diffMs = to - from;
                const hours = (diffMs / (1000 * 60 * 60)).toFixed(2);

                if (hours <= 0) {
                    setError('End time must be after start time');
                } else if (employeeLimits?.permission_hour > 0 && hours > employeeLimits.permission_hour) {
                    setError(`Permission cannot exceed ${employeeLimits.permission_hour} hours as per company policy`);
                } else if (employeeLimits?.min_hour_for_present > 0 && hours >= employeeLimits.min_hour_for_present) {
                    setError(`Permission duration must be less than ${employeeLimits.min_hour_for_present} hours. This requires a leave request instead.`);
                } else {
                    setError('');
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.emp_id) {
            setError('Please select an employee');
            return;
        }

        if (!formData.permission_date) {
            setError('Please select a date');
            return;
        }

        if (!formData.from_time || !formData.to_time) {
            setError('Please select both start and end time');
            return;
        }

        if (!formData.reason.trim()) {
            setError('Please provide a reason');
            return;
        }

        const from = new Date(`1970-01-01T${formData.from_time}`);
        const to = new Date(`1970-01-01T${formData.to_time}`);
        const hours = (to - from) / (1000 * 60 * 60);

        if (hours <= 0) {
            setError('End time must be after start time');
            return;
        }

        if (employeeLimits?.permission_hour > 0 && hours > employeeLimits.permission_hour) {
            setError(`Permission cannot exceed ${employeeLimits.permission_hour} hours as per company policy`);
            return;
        }

        if (employeeLimits?.min_hour_for_present > 0 && hours >= employeeLimits.min_hour_for_present) {
            setError(`Permission duration must be less than ${employeeLimits.min_hour_for_present} hours. This requires a leave request instead.`);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}prequest/add_permission_request.php`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                alert('Permission request submitted successfully');

                // Reset form
                setFormData({
                    emp_id: '',
                    permission_date: new Date().toISOString().split('T')[0],
                    from_time: '',
                    to_time: '',
                    reason: ''
                });
                setSearchQuery('');
                setSelectedEmployee(null);
                setEmployeeLimits(null);
                setWeeklyOffDays([]);

                onPermissionAdded();
                onHide();
            } else {
                setError(response.data.error || 'Failed to submit permission request');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to submit permission request');
            console.error('Error submitting permission request:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateHours = (fromTime, toTime) => {
        if (!fromTime || !toTime) return 0;
        const from = new Date(`1970-01-01T${fromTime}`);
        const to = new Date(`1970-01-01T${toTime}`);
        const diffMs = to - from;
        return (diffMs / (1000 * 60 * 60)).toFixed(2);
    };

    const getDayName = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'long' });
    };

    const handleFocus = () => {
        if (searchQuery && filteredEmployees.length > 0) {
            setShowSuggestions(true);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    <i className="bi bi-plus-circle me-2"></i>
                    New Permission Request
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" className="d-flex align-items-center">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>{error}</div>
                        </Alert>
                    )}

                    {employeeLimits && (
                        <Alert variant="info" className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Employee Limits:</strong>
                                    {employeeLimits.permission_per_month > 0 && (
                                        <span className="ms-2">
                                            <i className="bi bi-calendar-check me-1"></i>
                                            Max {employeeLimits.permission_per_month} per month
                                        </span>
                                    )}
                                    {employeeLimits.permission_hour > 0 && (
                                        <span className="ms-2">
                                            <i className="bi bi-clock me-1"></i>
                                            Max {employeeLimits.permission_hour} hours per permission
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Alert>
                    )}

                    <Row className="g-3">
                        {/* Employee Search with Autocomplete */}
                        <Col md={12}>
                            <Form.Group controlId="employeeSearch" ref={searchRef}>
                                <Form.Label>Search Employee <span className="text-danger">*</span></Form.Label>
                                <div className="position-relative">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <Form.Control
                                            type="text"
                                            placeholder="Type employee name, code, or email..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            onFocus={handleFocus}
                                            required
                                            autoComplete="off"
                                        />
                                        {searchQuery && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    setFormData(prev => ({ ...prev, emp_id: '' }));
                                                    setSelectedEmployee(null);
                                                    setShowSuggestions(false);
                                                }}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>

                                    {/* Autocomplete Suggestions */}
                                    {showSuggestions && filteredEmployees.length > 0 && (
                                        <div
                                            ref={suggestionsRef}
                                            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                                            style={{
                                                zIndex: 1050,
                                                maxHeight: '300px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            <div className="list-group list-group-flush">
                                                {filteredEmployees.map((employee) => (
                                                    <button
                                                        key={employee.emp_id}
                                                        type="button"
                                                        className="list-group-item list-group-item-action border-0 py-2 px-3"
                                                        onClick={() => handleEmployeeSelect(employee)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {employee.first_name} {employee.last_name}
                                                                </div>
                                                                <small className="text-muted">
                                                                    Code: {employee.emp_code} | ID: {employee.emp_id}
                                                                </small>
                                                            </div>
                                                            <div className="text-end">
                                                                <small className="text-success">
                                                                    {employee.department || 'No Department'}
                                                                </small>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {employee.email || 'No email'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Selected Employee Info */}
                                {selectedEmployee && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="mb-1 fw-bold">
                                                    <i className="bi bi-person-check me-2 text-success"></i>
                                                    Selected Employee
                                                </h6>
                                                <p className="mb-0">
                                                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                                                    <span className="text-muted ms-2">
                                                        ({selectedEmployee.emp_code}) | ID: {selectedEmployee.emp_id}
                                                    </span>
                                                </p>
                                                <small className="text-muted">
                                                    <i className="bi bi-envelope me-1"></i>
                                                    {selectedEmployee.email || 'No email'} |
                                                    <i className="bi bi-phone ms-2 me-1"></i>
                                                    {selectedEmployee.phone || 'No phone'}
                                                </small>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => {
                                                    setSelectedEmployee(null);
                                                    setSearchQuery('');
                                                    setFormData(prev => ({ ...prev, emp_id: '' }));
                                                }}
                                            >
                                                <i className="bi bi-x"></i> Clear
                                            </button>
                                        </div>
                                        {weeklyOffDays.length > 0 && (
                                            <div className="mt-2">
                                                <small className="text-muted">
                                                    <i className="bi bi-calendar-x me-1"></i>
                                                    Weekly off: {weeklyOffDays.join(', ')}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        {/* Hidden input for emp_id */}
                        <Form.Control
                            type="hidden"
                            name="emp_id"
                            value={formData.emp_id}
                            required
                        />

                        <Col md={6}>
                            <Form.Group controlId="permission_date">
                                <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="permission_date"
                                    value={formData.permission_date}
                                    onChange={handleChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    required
                                />
                                {formData.permission_date && (
                                    <Form.Text className={weeklyOffDays.includes(getDayName(formData.permission_date)) ? 'text-danger' : 'text-muted'}>
                                        {getDayName(formData.permission_date)}
                                        {weeklyOffDays.includes(getDayName(formData.permission_date)) && ' (Weekly Off)'}
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group controlId="from_time">
                                <Form.Label>From Time <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="time"
                                    name="from_time"
                                    value={formData.from_time}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group controlId="to_time">
                                <Form.Label>To Time <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="time"
                                    name="to_time"
                                    value={formData.to_time}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group controlId="reason">
                                <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="reason"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    placeholder="Briefly explain the reason for permission..."
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Please provide a clear and concise reason for the permission request.
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        {/* Summary Card */}
                        <Col md={12}>
                            <div className="card bg-light border-0">
                                <div className="card-header bg-transparent border-0 pb-0">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="bi bi-card-checklist me-2"></i>
                                        Request Summary
                                    </h6>
                                </div>
                                <div className="card-body py-2">
                                    <Row>
                                        <Col md={6}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted">
                                                    <i className="bi bi-clock me-2"></i>
                                                    Duration:
                                                </span>
                                                <span className="fw-bold">
                                                    {formData.from_time && formData.to_time ? (
                                                        <>
                                                            {calculateHours(formData.from_time, formData.to_time)} hours
                                                        </>
                                                    ) : '-- hours'}
                                                </span>
                                            </div>
                                            {employeeLimits?.permission_hour > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="text-muted">
                                                        <i className="bi bi-speedometer2 me-2"></i>
                                                        Hour Limit:
                                                    </span>
                                                    <span className={`fw-bold ${formData.from_time && formData.to_time &&
                                                        calculateHours(formData.from_time, formData.to_time) > employeeLimits.permission_hour
                                                        ? 'text-danger'
                                                        : 'text-info'
                                                        }`}>
                                                        {employeeLimits.permission_hour} hours
                                                    </span>
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            {employeeLimits?.min_hour_for_present > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="text-muted">
                                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                                        Leave Threshold:
                                                    </span>
                                                    <span className="fw-bold text-warning">
                                                        ≥ {employeeLimits.min_hour_for_present} hours
                                                    </span>
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted">
                                                    <i className="bi bi-calendar3 me-2"></i>
                                                    Request Date:
                                                </span>
                                                <span className="fw-bold">
                                                    {formData.permission_date ?
                                                        (() => {
                                                            const d = new Date(formData.permission_date);
                                                            const day = String(d.getDate()).padStart(2, '0');
                                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                                            const year = d.getFullYear();
                                                            return `${day}/${month}/${year}`;
                                                        })() : '--'
                                                    }
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={
                            loading ||
                            !formData.emp_id ||
                            !formData.permission_date ||
                            !formData.from_time ||
                            !formData.to_time ||
                            !formData.reason.trim() ||
                            (weeklyOffDays.length > 0 && weeklyOffDays.includes(getDayName(formData.permission_date)))
                        }
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send me-2"></i>
                                Submit Request
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddPermissionRequest;